import axios from 'axios';
import { toCamelCase } from '../../utils/formatters';
import { getAccessToken } from '../../utils/auth';
import { TechnicalErrorCode } from '../../constants';
/**
 * Map `pathParams` values to their corresponding keys in the `url`
 *
 * @param config The request configuration object
 *
 * @example
 * ```typescript
 *    const url = "/thread/:threadId";
 *    const pathParams = {threadId:10};
 *    const result = mapPathParamsToURL({url,pathParams})
 *    console.log(result);  // "/thread/10"
 * ```
 *
 * @return Mapped URL to its pathParams
 */
const mapPathParamsToURL = ({ url, pathParams }) => {
    if (!(url && pathParams)) {
        return url;
    }
    let result = url;
    Object.entries(pathParams || {}).forEach(([k, v]) => {
        result = result.replace(`:${k}`, encodeURIComponent(v));
    });
    return result;
};
/**
 * Construct headers object for axios request
 *
 * @returns {AxiosRequestHeaders} Constructed headers object
 */
const buildConfigHeaders = ({ headers, overrideAuthorizationToken = false, }) => {
    const token = getAccessToken();
    const builtHeaders = Object.assign({ 'Content-Type': 'application/json' }, headers);
    if (token && !overrideAuthorizationToken) {
        builtHeaders['Authorization'] = `Bearer ${token}`;
    }
    return builtHeaders;
};
export const createClient = (baseURL, timeout, isCaseMapperEnabled) => {
    const api = axios.create({ baseURL, timeout });
    api.interceptors.request.use((config) => {
        config.headers = buildConfigHeaders(config);
        config.url = mapPathParamsToURL(config);
        return config;
    }, (error) => Promise.reject(error));
    api.interceptors.response.use((res) => {
        const response = {
            data: isCaseMapperEnabled ? toCamelCase(res.data) : res.data,
            headers: res.headers,
            status: res.status,
        };
        return response;
    }, (err) => {
        let appError = null;
        if (err === null || err === void 0 ? void 0 : err.response) {
            if (Array.isArray(err === null || err === void 0 ? void 0 : err.response.data)) {
                appError = {
                    status: err === null || err === void 0 ? void 0 : err.response.status,
                    error: err === null || err === void 0 ? void 0 : err.response.data[0].code,
                    message: err === null || err === void 0 ? void 0 : err.response.data[0].message,
                    errorList: err === null || err === void 0 ? void 0 : err.response.data,
                };
            }
            else {
                /**
                 * The request was made and the server responded with a
                 * status code that falls out of the range of 2xx
                 */
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const { status, data: { frontend_error_code: errorCode, error, message, code }, } = err.response;
                appError = {
                    status,
                    message,
                    error,
                    code: errorCode || code,
                };
            }
        }
        else if (!err || typeof err.response === 'undefined') {
            /**
             * The request was made but no response was received, `error.request`
             * is an instance of XMLHttpRequest in the browser and an instance
             * of http.ClientRequest in Node.js
             */
            appError = {
                status: 400,
                error: 'Invalid Request Payload',
                message: 'A technical error occurred.',
                code: TechnicalErrorCode,
            };
        }
        return Promise.reject(appError);
    });
    return api;
};
//# sourceMappingURL=client.js.map